import React, { useEffect } from "react"
import Head from "next/head"
import AemGrid from "@/components/AemGrid"
import {
  MODEL_JSON_EXTENSION,
  footerUrl,
  FRAGMENT_TYPES,
  headerUrl,
  publicWebsiteUrl,
} from "@/constants"
import {
  getAemServerAxiosInstance,
  getAemServerModelJsonAxiosInstance,
} from "@/services/server/aem.server"
import { useDispatch } from "react-redux"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { i18n } from "next-i18next"
import {
  filterBackEndJson,
  generateCanonicalUrl,
  getClienIP,
  getDataLayerObj,
} from "@/utils/helper"
import ProductAccordion from "@/components/ProductDetail/v3/ProductAccordion"
import BreadCrumb from "@/components/Default/BreadCrumb"
import { setGbhDataLayerCustom } from "@/store/features/genericSlice"
import usePageAuth from "@/hooks/usePageAuth"
import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"
import UpsellInstallServices from "@/components/Shared/UpsellInstallServices"
import Script from "next/script"
const Home = props => {
  const {
    tmpData: data,
    headertmp: header,
    footertmp: footer,
    ip,
    globalScript,
    footerScript,
    analyticsData,
    gbhDataLayerCustom,
    homeApplicationJSONSchema,
    seoProperties,
    fullUrl,
    siteName = "",
  } = props
  const dispatch = useDispatch()
  const brandLogoImg = homeApplicationJSONSchema
    ? JSON.parse(homeApplicationJSONSchema).logo
    : ""
  useEffect(() => {
    if (ip) sessionStorage.setItem("true-ip", ip)
  }, [ip])
  usePageAuth()
  useEffect(() => {
    getDataLayerObj(analyticsData)
  }, [analyticsData])
  useEffect(() => {
    dispatch(setGbhDataLayerCustom(gbhDataLayerCustom))
    return () => dispatch(setGbhDataLayerCustom(""))
  }, [])

  return (
    <>
      <Head>
        {data?.description && (
          <>
            <meta name="description" content={data?.description} />
            <meta property="og:description" content={data?.description} />
          </>
        )}
        {homeApplicationJSONSchema ? (
          <script type="application/ld+json">
            {homeApplicationJSONSchema}
          </script>
        ) : null}
      </Head>
      <>
        <GlobalPageHeader
          themeType={data?.appliedCssClassNames}
          header={header}
          title={data.title}
          globalScripting={globalScript}
          fullUrl={fullUrl}
          phone={seoProperties?.phoneNum}
          imageUrl={brandLogoImg}
          siteName={siteName}
          data={data}
        />
        <AemGrid
          data={data}
          count={0}
          fragment={FRAGMENT_TYPES.MAIN}
          breadCrumb={(data, fullData) => (
            <BreadCrumb data={data} fullData={fullData} />
          )}
          productAccordion={data => <ProductAccordion data={data} />}
          upSellContainer={data => <UpsellInstallServices authData={data} />}
        />
        <GlobalPageFooter footer={footer} footerScripting={footerScript} />
        <Script id="disableScrollRestoration">{`window.history.scrollRestoration = "manual"`}</Script>
      </>
    </>
  )
}
/**
 *
 * @return {Object}
 */
export async function getServerSideProps({ req, res, locale }) {
  const { url = "" } = req
  const serverAemApi = getAemServerAxiosInstance()
  const serverAemModelJsonApi = getAemServerModelJsonAxiosInstance()
  await i18n?.reloadResources()
  const ip = getClienIP(req)
  const urlPath = req.url.split("?")[0] ?? ""
  const { data } = await serverAemModelJsonApi({
    url: `${locale}${MODEL_JSON_EXTENSION}`,
    method: "GET",
    headers: {
      IpAddress: ip,
    },
  })
  const seoProperties = data?.seoProperties || ""
  const siteName = data?.siteName
  const { data: header } = await serverAemApi({
    url: `${data.headerExpFrg ? data.headerExpFrg : headerUrl}.model.json`,
    method: "GET",
    ...{
      headers: {
        IpAddress: ip,
      },
    },
  })
  const { data: footer } = await serverAemApi({
    url: `${data.footerExpFrg ? data.footerExpFrg : footerUrl}.model.json`,
    method: "GET",
    ...{
      headers: {
        IpAddress: ip,
      },
    },
  })
  const globalScript = data?.globalScript || null
  const footerScript = data?.footerScript || null
  const topHeadScript = data?.topHeadScript ? data?.topHeadScript : null
  const analyticsScriptClass = data?.analyticsScriptClass
    ? data?.analyticsScriptClass
    : null
  let tmpData = {}
  if (data.pageComponentProperties) {
    tmpData = data.pageComponentProperties
  } else {
    tmpData = data
  }
  const analyticsData = data?.data
  const gbhDataLayerCustom = {}
  if (data?.datalayerPageName) {
    gbhDataLayerCustom.datalayerPageName = data?.datalayerPageName
  }
  if (data?.eventPageType) {
    gbhDataLayerCustom.eventPageType = data?.eventPageType
  }
  if (data?.pageType) {
    gbhDataLayerCustom.pageType = data?.pageType
  }
  if (
    !data?.pageComponentProperties?.description &&
    data?.pageComponentProperties?.dataLayer &&
    data?.pageComponentProperties?.dataLayer[data?.pageComponentProperties?.id]
  ) {
    gbhDataLayerCustom.dataLayer =
      data?.pageComponentProperties?.dataLayer[
        data?.pageComponentProperties?.id
      ]
  }
  tmpData = filterBackEndJson(tmpData)
  const headertmp = filterBackEndJson(header)
  const footertmp = filterBackEndJson(footer)
  const metaContent = data?.metaContent ?? ""
  const homeApplicationJSONSchema =
    data?.pageComponentProperties.id === "home" ? data?.schemaJsonLD : ""
  const canonicalUrl = data?.canonicalUrl ?? ""
  const fullUrl = generateCanonicalUrl(url, locale, publicWebsiteUrl)
  const origin = publicWebsiteUrl
  // Pass data to the page via props
  return {
    props: {
      ip,
      tmpData,
      headertmp,
      footertmp,
      metaContent,
      globalScript,
      footerScript,
      topHeadScript,
      analyticsData,
      analyticsScriptClass,
      gbhDataLayerCustom,
      urlPath,
      homeApplicationJSONSchema,
      canonicalUrl,
      seoProperties,
      fullUrl,
      origin,
      siteName,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  }
}
export default Home
